'use client';

import { cn } from '@app/ui/_lib/utils';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { Container } from '../Container';
import { IconResolver, type IconType } from '../IconResolver';
import { Text } from '../Text';
import type { CSSProperties, ReactNode } from 'react';
import { TextSectionBlock, type TextSectionBlockProps } from '../TextSectionBlock';

export type FeaturesGridProps = {
	className?: string;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	features?: {
		icon?: IconType;
		title?: ReactNode;
		description?: string;
		button?: ComponentButtonProps;
	}[];
	numberOfColumns?: number;
};

export function FeaturesGrid(props: FeaturesGridProps) {
	let mdCols = 2;
	let lgCols = 3;

	if (props.numberOfColumns !== undefined) {
		if (props.numberOfColumns < mdCols) {
			mdCols = props.numberOfColumns;
		}

		lgCols = props.numberOfColumns;
	}

	return (
		<section className={props.className} data-component='FeaturesGrid'>
			<Container className='flex flex-col gap-spacing-2xl'>
				<div>{props.textSectionBlock && <TextSectionBlock className={`max-w-[54rem]`} size='lg' {...props.textSectionBlock} />}</div>

				{props.features && props.features.length > 0 && (
					<ul
						className='grid gap-spacing-lg md:grid-cols-(--md-cols) lg:grid-cols-(--lg-cols) lg:gap-x-spacing-2xl'
						style={{ '--md-cols': `repeat(${mdCols}, minmax(0, 1fr))`, '--lg-cols': `repeat(${lgCols}, minmax(0, 1fr))` } as CSSProperties}
					>
						{props.features.map((item, index) => (
							<li className='flex flex-col items-start md:py-spacing-lg' key={`${item.icon}-${index}-features-grid`}>
								<IconResolver
									className={cn(
										'text-foreground-brand-primary flex-none mb-spacing-xs w-6 h-6',
										typeof item.icon === 'object' ? 'w-1/2 max-w-none! max-h-none!' : '',
									)}
									icon={item.icon ?? 'circle'}
								/>

								<div className='flex flex-col gap-spacing-3xs'>
									{item.title && (
										<Text as='h3' styleType='body-2xl-medium' className='text-text-primary'>
											{item.title}
										</Text>
									)}

									{item.description && (
										<Text styleType='body-lg' className='text-text-secondary whitespace-pre-wrap'>
											{item.description}
										</Text>
									)}
								</div>

								{item.button && <ComponentButton className='mt-spacing-md' {...item.button} />}
							</li>
						))}
					</ul>
				)}
			</Container>
		</section>
	);
}
